import { computed } from 'vue'
import { useStore } from 'vuex'
import { sidebarPathAllowed, isWorkspaceOwnerUser, isClinchPlatformOnlyPath } from '@/utils/planEntitlements'
import { isTenantWorkspaceHost } from '@/utils/saasPlatform'

const SIDEBAR_MODULES = [
  { module: 'dashboard', resource: 'overview', path: '/dashboard', label: 'Dashboard', icon: 'fas fa-home', exact: true },
  { module: 'dashboard', resource: 'business', path: '/dashboard', label: 'Dashboard', icon: 'fas fa-home', exact: true },
  { module: 'listing_management', resource: 'business', path: '/manage/listing-management', label: 'Listing Management', icon: 'fas fa-list-alt' },
  { module: 'listing_management', resource: 'store_alerts', path: '/manage/alerts', label: 'SMC', icon: 'fas fa-warehouse' },

  {
    module: 'tickets',
    resource: 'issue_tickets',
    path: '/manage/tickets',
    label: 'Tickets',
    icon: 'fas fa-inbox',
    altResource: 'support_tickets',
  },
  { module: 'team_inbox', resource: 'conversations', path: '/manage/inbox', label: 'Team Inbox', icon: 'fab fa-whatsapp' },
  { module: 'team_inbox', resource: 'conversations', path: '/manage/contacts', label: 'Contacts', icon: 'fas fa-address-book' },
  { module: 'team_inbox', resource: 'templates', path: '/manage/templates', label: 'Templates', icon: 'fas fa-file-alt' },
  {
    module: 'team_inbox',
    resource: 'broadcasts',
    path: '/manage/templates?tab=broadcasts',
    label: 'Campaigns',
    icon: 'fas fa-bullhorn',
  },
  {
    module: 'team_inbox',
    resource: 'waba_accounts',
    path: '/manage/company-wabas',
    label: 'WhatsApp Account',
    icon: 'fab fa-whatsapp',
  },
  { module: 'wallets', resource: 'wallets', path: '/manage/wallets', label: 'Wallets', icon: 'fas fa-wallet' },
  { module: 'wallets', resource: 'withdrawals', path: '/manage/withdrawals', label: 'Withdrawals', icon: 'fas fa-money-check-alt' },
  {
    module: 'finance',
    resource: 'ledger',
    path: '/manage/xero-accounting',
    label: 'Xero accounting',
    icon: 'fas fa-file-invoice-dollar',
    clinchPlatformOnly: true,
  },
  { module: 'marketing', resource: 'campaigns', path: '/manage/promotions', label: 'Promotions', icon: 'fas fa-bullhorn' },
  { module: 'listing_management', resource: 'categories', path: '/manage/categories', label: 'Categories', icon: 'fas fa-tags' },
  { module: 'operations', resource: 'products', path: '/dashboard/products', label: 'Products', icon: 'fas fa-box' },
  { module: 'operations', resource: 'orders', path: '/dashboard/orders', label: 'Orders', icon: 'fas fa-shopping-cart' },
  {
    module: 'system_management',
    resource: 'users',
    path: '/manage/system',
    label: 'System Management',
    icon: 'fas fa-cogs',
    altResource: 'user_types',
    altResources: ['shopping_agent_mcp', 'api_documentation'],
  },
]

/** Merchant business_owner — uses My Businesses, not platform Listing Management. */
export function isBusinessOwnerUser(user) {
  if (!user) return false
  const code = user.user_type?.code || user.user_type
  return code === 'business_owner'
}

function isBusinessListingSidebarDef(def) {
  return (
    def.module === 'listing_management' &&
    def.resource === 'business' &&
    def.path === '/manage/listing-management'
  )
}

/** Other System Management resources. API-docs-only staff see that name in the sidebar. */
const SYSTEM_MANAGEMENT_OTHER_RESOURCES = ['users', 'user_types', 'audit', 'shopping_agent_mcp']

function systemManagementNavLabel(permissions) {
  const hasApiDocs = canAccessResource(permissions, 'system_management', 'api_documentation')
  if (!hasApiDocs) return 'System Management'
  const hasOther = SYSTEM_MANAGEMENT_OTHER_RESOURCES.some((resource) =>
    canAccessResource(permissions, 'system_management', resource)
  )
  return hasOther ? 'System Management' : 'API Documentation'
}

/** Listing admin routes require read; update/approve-only must not add sidebar links. */
function canShowSidebarNavItem(permissions, def) {
  if (def.module === 'listing_management') {
    return (
      canAction(permissions, def.module, def.resource, 'read') ||
      (def.altResource && canAction(permissions, def.module, def.altResource, 'read'))
    )
  }
  const altResources = [
    ...(def.altResource ? [def.altResource] : []),
    ...(Array.isArray(def.altResources) ? def.altResources : []),
  ]
  return (
    canAccessResource(permissions, def.module, def.resource) ||
    altResources.some((resource) => canAccessResource(permissions, def.module, resource))
  )
}

/** Staff nav from permission matrix (System Management last). */
export function getStaffSidebarItems(permissions) {
  const items = []
  const seen = new Set()
  for (const def of SIDEBAR_MODULES) {
    if (def.module === 'dashboard') continue
    // Clinch-only links (Xero, marketplace wallets, …) never appear on SaaS Hosts.
    if (def.clinchPlatformOnly || isClinchPlatformOnlyPath(def.path)) {
      if (isTenantWorkspaceHost()) continue
    }
    const allowed = canShowSidebarNavItem(permissions, def)
    if (!allowed) continue
    if (seen.has(def.path)) continue
    seen.add(def.path)
    const label =
      isBusinessListingSidebarDef(def) && isTenantWorkspaceHost()
        ? 'Stores'
        : def.path === '/manage/system'
          ? systemManagementNavLabel(permissions)
          : def.label
    items.push({
      path: def.path,
      label,
      icon: def.icon,
      exact: def.exact || false,
    })
  }
  return items
}

/** Business wallet read + create: may open a store's Wallets tab and submit a payout request. */
export function canRequestStorePayout(user) {
  if (!user) return false
  if (user.is_superuser) return true
  const perms = user.permissions || []
  return (
    canAction(perms, 'wallets', 'business_wallet', 'read') &&
    canAction(perms, 'wallets', 'business_wallet', 'create')
  )
}

/** Store page on the Wallets tab: /manage/businesses for listing staff, else the owner route. */
export function storeWalletTabPath(user, businessId) {
  const useManage =
    !isBusinessOwnerUser(user) &&
    (!!user?.is_superuser || canAction(user?.permissions || [], 'listing_management', 'business', 'read'))
  const base = useManage ? '/manage/businesses' : '/user/my-business'
  return `${base}/${businessId}?tab=wallets`
}

/** Workspace feature pages (branding, billing, settlement, onboarding): superuser or the ticked resource. */
export function canOpenWorkspaceFeature(user, resource) {
  if (!user) return false
  return !!user.is_superuser || canAccessResource(user.permissions || [], 'workspace', resource)
}

/** Clinch Open-workspace: superuser, platform admin, or Assist client workspaces update. */
export function canAssistClientWorkspaces(user) {
  if (!user) return false
  if (user.is_superuser) return true
  if (isPlatformAdminUserType(user)) return true
  return canAction(user.permissions || [], 'workspace', 'support', 'update')
}

/** Full sidebar menu including dashboard and owner My Businesses. */
export function getSidebarMenuItems(user) {
  const perms = user?.permissions || []
  const items = []
  const seen = new Set()
  const push = (item) => {
    if (seen.has(item.path)) return
    seen.add(item.path)
    items.push(item)
  }

  if (
    canAction(perms, 'dashboard', 'overview', 'read') ||
    canAction(perms, 'dashboard', 'business', 'read')
  ) {
    push({ path: '/dashboard', label: 'Dashboard', icon: 'fas fa-home', exact: true })
  }

  // Workspace features: superusers and System Admin get these by default; other roles need them ticked.
  const tenantHost = isTenantWorkspaceHost()
  const canWorkspace = (resource) => canOpenWorkspaceFeature(user, resource)
  if (!tenantHost && canWorkspace('ai_reporting')) {
    push({ path: '/manage/reporting', label: 'AI Reporting', icon: 'fas fa-chart-line' })
  }
  if (canWorkspace('branding')) {
    push({ path: '/manage/appearance', label: 'Platform branding', icon: 'fas fa-palette' })
  }
  if (tenantHost && canWorkspace('billing')) {
    push({ path: '/manage/subscription', label: 'Usage & billing', icon: 'fas fa-credit-card' })
  }
  if (tenantHost && canWorkspace('settlement')) {
    push({ path: '/manage/settlement', label: 'Money & payouts', icon: 'fas fa-balance-scale' })
  }
  if (isWorkspaceOwnerUser(user)) {
    push({ path: '/manage/store-wallet', label: 'Wallet', icon: 'fas fa-wallet' })
  }
  if (tenantHost && canWorkspace('onboarding')) {
    push({ path: '/onboarding', label: 'Set up My Platform', icon: 'fas fa-clipboard-check' })
  }
  if (
    isTenantWorkspaceHost() &&
    (canAccessResource(perms, 'wallets', 'withdrawals') || canAccessResource(perms, 'wallets', 'wallets'))
  ) {
    push({ path: '/manage/store-wallet', label: 'Wallet', icon: 'fas fa-wallet' })
  }

  // Clinch platform ops — never on SaaS client workspace Hosts. Each page has its own Workspaces tick.
  // Assist client workspaces (update) also unlocks Clients list read without the Clients tick.
  if (!isTenantWorkspaceHost()) {
    const platformOps = [
      { path: '/manage/clients', label: 'Client platforms', icon: 'fas fa-globe', resource: 'clients' },
      { path: '/manage/credit-monitor', label: 'Credit monitoring', icon: 'fas fa-gauge-high', resource: 'credit_monitor' },
      { path: '/manage/debit-orders', label: 'Debit runs', icon: 'fas fa-file-invoice-dollar', resource: 'billing_collections' },
      {
        path: '/manage/payment-proofs',
        label: 'Payment proofs',
        icon: 'fas fa-file-invoice',
        resource: 'billing_collections',
        altResources: ['clients'],
      },
      { path: '/manage/settlements', label: 'Platform commerce overview', icon: 'fas fa-sitemap', resource: 'commerce_overview' },
      { path: '/manage/packages', label: 'Plans', icon: 'fas fa-box-open', resource: 'plans' },
      { path: '/manage/billing-rates', label: 'Billing rates', icon: 'fas fa-tags', resource: 'billing_rates' },
    ]
    for (const { resource, altResources, ...item } of platformOps) {
      const canRead =
        canAction(perms, 'workspace', resource, 'read')
        || (Array.isArray(altResources)
          && altResources.some((alt) => canAction(perms, 'workspace', alt, 'read')))
      const assistClients =
        (resource === 'clients' || item.path === '/manage/payment-proofs')
        && canAction(perms, 'workspace', 'support', 'update')
      if ((canRead || assistClients) && sidebarPathAllowed(user, item.path)) push(item)
    }
  }

  if (isBusinessOwnerUser(user)) {
    push({
      path: '/user/my-businesses',
      label: 'My Businesses',
      icon: 'fas fa-building',
      exact: false,
    })
  }

  for (const item of getStaffSidebarItems(perms)) {
    if (isBusinessOwnerUser(user) && item.path === '/manage/listing-management') {
      continue
    }
    if (!sidebarPathAllowed(user, item.path)) {
      continue
    }
    push(item)
  }
  // Help & guides: every signed-in user; the guides themselves are filtered by role.
  if (user) {
    push({ path: '/help', label: 'Help & guides', icon: 'fas fa-life-ring' })
  }
  return items
}

/**
 * Folders in the expanded sidebar. Dashboard, Tickets, System, and Help stay as standalone links.
 * Empty groups are omitted. Leftover permissioned links stay visible.
 */
export const SIDEBAR_NAV_GROUPS = [
  {
    id: 'whatsapp',
    label: 'WhatsApp',
    icon: 'fab fa-whatsapp',
    paths: [
      '/manage/inbox',
      '/manage/contacts',
      '/manage/templates',
      '/manage/templates?tab=broadcasts',
      '/manage/company-wabas',
    ],
  },
  {
    id: 'store',
    label: 'Store',
    icon: 'fas fa-store',
    paths: [
      '/dashboard/orders',
      '/dashboard/products',
      '/manage/listing-management',
      '/manage/alerts',
      '/user/my-businesses',
      '/manage/categories',
      '/manage/promotions',
    ],
  },
  {
    id: 'money',
    label: 'Wallet',
    icon: 'fas fa-wallet',
    paths: [
      '/manage/store-wallet',
      '/manage/wallets',
      '/manage/withdrawals',
      '/manage/xero-accounting',
    ],
  },
  {
    id: 'workspaces',
    label: 'Platforms',
    icon: 'fas fa-layer-group',
    paths: [
      '/manage/clients',
      '/manage/credit-monitor',
      '/manage/debit-orders',
      '/manage/payment-proofs',
      '/manage/settlements',
      '/manage/packages',
      '/manage/billing-rates',
      '/manage/appearance',
      '/manage/subscription',
      '/manage/settlement',
      '/onboarding',
    ],
  },
]

/** Expanded-sidebar order: daily usage first; AI Reporting stays a top pin on Clinch platform. */
const SIDEBAR_NAV_LAYOUT = [
  { type: 'item', path: '/dashboard' },
  { type: 'group', id: 'whatsapp' },
  { type: 'item', path: '/manage/reporting' },
  { type: 'group', id: 'store' },
  { type: 'item', path: '/manage/tickets' },
  { type: 'group', id: 'money' },
  { type: 'group', id: 'workspaces' },
  { type: 'item', path: '/manage/system' },
  { type: 'item', path: '/help' },
]

function sidebarItemMatchesPath(itemPath, currentPath) {
  if (!itemPath || !currentPath) return false
  if (itemPath === '/dashboard') return currentPath === '/dashboard'
  return currentPath === itemPath || currentPath.startsWith(`${itemPath}/`)
}

/** Grouped sidebar. Dashboard, Tickets, System, and Help stay as standalone links. */
export function getSidebarNavSections(user) {
  const items = getSidebarMenuItems(user)
  const byPath = new Map(items.map((item) => [item.path, item]))
  const used = new Set()

  const groupsById = {}
  const groups = []
  for (const group of SIDEBAR_NAV_GROUPS) {
    const groupItems = []
    for (const path of group.paths) {
      const item = byPath.get(path)
      if (!item) continue
      groupItems.push(item)
      used.add(path)
    }
    if (groupItems.length) {
      const built = {
        id: group.id,
        label: group.id === 'workspaces' && isTenantWorkspaceHost() ? 'My Platform' : group.label,
        icon: group.icon,
        items: groupItems,
      }
      groupsById[group.id] = built
      groups.push(built)
    }
  }

  const rows = []
  for (const slot of SIDEBAR_NAV_LAYOUT) {
    if (slot.type === 'group') {
      const group = groupsById[slot.id]
      if (!group) continue
      rows.push({ type: 'group', key: `group-${group.id}`, group })
      continue
    }
    const item = byPath.get(slot.path)
    if (!item || used.has(item.path)) continue
    used.add(item.path)
    rows.push({ type: 'item', key: item.path, item })
  }

  for (const item of items) {
    if (used.has(item.path)) continue
    used.add(item.path)
    rows.push({ type: 'item', key: item.path, item })
  }

  return {
    pinned: rows.filter((row) => row.type === 'item').map((row) => row.item),
    groups,
    rows,
    flatItems: items,
  }
}

export function sidebarGroupIdForPath(currentPath, sections) {
  const groups = sections?.groups || []
  for (const group of groups) {
    if (group.items.some((item) => sidebarItemMatchesPath(item.path, currentPath))) {
      return group.id
    }
  }
  return ''
}

function findPerm(permissions, module, resource) {
  return (permissions || []).find(
    (p) => p.module === module && p.resource === resource
  )
}

/** True if user can approve, reject, or release on this resource (review queue access). */
export function canReviewResource(permissions, module, resource) {
  return (
    canAction(permissions, module, resource, 'approve') ||
    canAction(permissions, module, resource, 'reject') ||
    canAction(permissions, module, resource, 'release')
  )
}

export function canAction(permissions, module, resource, action = 'read') {
  const row = findPerm(permissions, module, resource)
  if (!row) return false
  if (action === 'read') return !!row.read
  if (action === 'create') return !!row.create
  if (action === 'update') return !!row.update
  if (action === 'delete') return !!row.delete
  if (action === 'approve') return !!row.approve
  if (action === 'reject') return !!row.reject
  if (action === 'release') return !!row.release
  return false
}

/** True if the user has any CRUD/workflow flag on this module resource (for nav/route access). */
export function canAccessResource(permissions, module, resource) {
  const row = findPerm(permissions, module, resource)
  if (!row) return false
  return permissionRowActive(row)
}

const OPERATOR_MODULES = new Set([
  'listing_management',
  'operations',
  'tickets',
  'team_inbox',
  'wallets',
  'finance',
  'system_management',
  'dashboard',
])

function permissionRowActive(p) {
  return !!(
    p?.read ||
    p?.create ||
    p?.update ||
    p?.delete ||
    p?.approve ||
    p?.reject ||
    p?.release
  )
}

/** True when the user may load the full platform business catalog (not scoped). */
export function hasResourceAllScope(user, module, resource) {
  if (!user) return false
  if (user.is_superuser) return true
  const row = findPerm(user.permissions || [], module, resource)
  return row?.scope === 'all'
}

/** Admin business list API vs linked ``my_listings`` for filters and dropdowns. */
export function shouldUsePlatformBusinessCatalog(user) {
  if (!user) return false
  if (isPlatformAdminUserType(user)) return true
  return hasResourceAllScope(user, 'listing_management', 'business')
}

/**
 * Prefer /manage/businesses… (platform) vs /user/my-business… (merchant).
 * Business owners always use owner routes, even if they landed on a /manage/ URL.
 */
export function prefersManageBusinessRoutes(user, routePath = '') {
  if (isBusinessOwnerUser(user)) return false
  if (shouldUsePlatformBusinessCatalog(user)) return true
  return typeof routePath === 'string' && routePath.startsWith('/manage/')
}

/** SaaS workspace admin (plan-scoped). Clinch operators on a tenant host are superusers. */
export { isWorkspaceOwnerUser }

/** Superuser or Clinch platform_admin / legacy admin. SaaS tenant staff use the permission matrix. */
export function isPlatformAdminUserType(user) {
  if (!user) return false
  if (user.is_superuser) return true
  if (isWorkspaceOwnerUser(user)) return false
  if (isTenantWorkspaceHost()) return false
  const code = user.user_type?.code || user.user_type
  return code === 'admin' || code === 'platform_admin'
}

/** Clinch operator UI (replaces legacy User.is_staff checks in the SPA). */
export function isPlatformOperator(user) {
  if (!user) return false
  if (isWorkspaceOwnerUser(user)) return false
  if (isPlatformAdminUserType(user)) return true
  const perms = user.permissions || []
  return perms.some((p) => {
    if (p.module === 'dashboard' && p.resource === 'business') return false
    if (!OPERATOR_MODULES.has(p.module)) return false
    return permissionRowActive(p)
  })
}

/** Users eligible for ticket/inbox assignment (internal staff types). */
export function isAssignableStaffUser(user) {
  if (!user || typeof user !== 'object') return false
  if (user.is_superuser) return true
  const ut = user.user_type
  if (ut && typeof ut === 'object' && ut.is_internal === true) return true
  const perms = user.permissions || []
  return (
    canAction(perms, 'tickets', 'issue_tickets', 'read') ||
    canAction(perms, 'tickets', 'support_tickets', 'read') ||
    canAction(perms, 'team_inbox', 'conversations', 'read')
  )
}

export function resolveDefaultRoute(user) {
  const perms = user?.permissions || []
  const isOwner = isBusinessOwnerUser(user)
  if (isOwner && canAction(perms, 'dashboard', 'business', 'read')) {
    return '/dashboard'
  }
  const seen = new Set()
  for (const item of SIDEBAR_MODULES) {
    const key = item.path
    if (seen.has(key)) continue
    if (isOwner && item.module === 'listing_management' && item.resource === 'business') continue
    const allowed = canShowSidebarNavItem(perms, item)
    if (allowed) {
      seen.add(key)
      return item.path
    }
  }
  if (isOwner) return '/user/my-businesses'
  return '/dashboard'
}

export function usePermissions() {
  const store = useStore()
  const permissions = computed(() => store.getters.currentUser?.permissions || [])
  const managedUserTypeCodes = computed(
    () => store.getters.currentUser?.managed_user_type_codes || []
  )

  function can(module, resource, action = 'read') {
    const user = store.getters.currentUser
    if (user?.is_superuser) return true
    return canAction(permissions.value, module, resource, action)
  }

  const sidebarModules = computed(() => getStaffSidebarItems(permissions.value))

  const canViewDashboard = computed(() => {
    return (
      can('dashboard', 'overview', 'read') ||
      can('dashboard', 'business', 'read')
    )
  })

  return {
    permissions,
    managedUserTypeCodes,
    can,
    sidebarModules,
    canViewDashboard,
    isPlatformOperator: () => isPlatformOperator(store.getters.currentUser),
    resolveDefaultRoute: () => resolveDefaultRoute(store.getters.currentUser),
  }
}

export default usePermissions
